import { ACTIVITY_ACTION_CATEGORY, ACTIVITY_LIMITS, type ActivityEventInput, type ClientActivityAction } from '@/contract/sponsor-activity.schema'

/* Spec PWA-033 D-06 — historico de atividade no app: troca de tela e clique em [data-activity]. Os eventos vao em lote para POST /activity (a cada 10 s, com 50 na fila, ao esconder a aba e ao sair).
   Sem rede, ficam guardados (ate 200) e vao depois. Nada aqui pode quebrar a tela: todo erro e engolido.
   Nunca grava o que o padrinho digita: so o padrao da rota, ids e o nome do elemento marcado. */

export const VISIT_IDLE_MS = 30 * 60 * 1000
export const FLUSH_EVERY_MS = 10_000
export const MAX_STORED = 200
const VISIT_KEY = 'gpd-visit'
const PENDING_KEY = 'gpd-activity-pending'
const NAME = /^[a-z0-9][a-z0-9_.-]{0,39}$/
const TARGET = /^[A-Za-z0-9._:-]{1,80}$/

/** 'ok' enviado · 'retry' sem rede (guarda) · 'drop' recusado pelo servidor (nao adianta tentar de novo). */
export type SendResult = 'ok' | 'retry' | 'drop'
export type ActivitySender = (events: ActivityEventInput[], opts: { keepalive: boolean }) => Promise<SendResult>
type KV = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const safe = <T>(fn: () => T, fallback: T): T => {
    try {
        return fn()
    } catch {
        return fallback
    }
}

/** Padrao da rota sem os layouts sem caminho: '/_auth/_tabs/inicio' → '/inicio'. */
export const routePattern = (routeId: string) => `/${routeId.split('/').filter(s => s && !s.startsWith('_')).join('/')}`

/* Visita: renovada depois de 30 min parada. Guardada no sessionStorage (uma por aba). Tambem vai no cabecalho
   X-Visit-Id de toda chamada (lib/api.ts), para o servidor agrupar as buscas e atualizacoes na mesma visita. */
let memoryVisit: { id: string; lastAt: number } | null = null
export function visitId(now = Date.now(), storage: KV | null = safe(() => sessionStorage, null)): string {
    const stored = memoryVisit ?? safe(() => JSON.parse(storage?.getItem(VISIT_KEY) ?? 'null') as typeof memoryVisit, null)
    const fresh = stored && typeof stored.id === 'string' && now - stored.lastAt < VISIT_IDLE_MS ? stored : { id: crypto.randomUUID(), lastAt: now }
    memoryVisit = { id: fresh.id, lastAt: now }
    safe(() => storage?.setItem(VISIT_KEY, JSON.stringify(memoryVisit)), undefined)
    return fresh.id
}
/** So para os testes. */
export const resetVisit = () => { memoryVisit = null }

export function createActivityTracker({ send, now = Date.now, storage = safe(() => localStorage, null), visit = () => visitId(now()) }: {
    send: ActivitySender
    now?: () => number
    storage?: KV | null
    visit?: () => string
}) {
    let queue: ActivityEventInput[] = safe(() => {
        const saved = JSON.parse(storage?.getItem(PENDING_KEY) ?? '[]') as unknown
        return Array.isArray(saved) ? (saved as ActivityEventInput[]).slice(-MAX_STORED) : []
    }, [])
    /** Tela aberta agora (o evento sai quando ela fecha, com o tempo nela). */
    let screen: { route: string; target?: string; since: number; visitId: string } | null = null

    const persist = () => safe(() => (queue.length ? storage?.setItem(PENDING_KEY, JSON.stringify(queue)) : storage?.removeItem(PENDING_KEY)), undefined)

    function push(action: ClientActivityAction, extra: Omit<ActivityEventInput, 'category' | 'action' | 'occurredAt' | 'visitId'> & { at?: number; visitId?: string } = {}) {
        const { at, visitId: id, ...rest } = extra
        queue.push({ category: ACTIVITY_ACTION_CATEGORY[action], action, occurredAt: new Date(at ?? now()).toISOString(), visitId: id ?? visit(), ...rest } as ActivityEventInput)
        if (queue.length > MAX_STORED) queue = queue.slice(-MAX_STORED)
        if (queue.length >= ACTIVITY_LIMITS.batch) void flush()
    }

    let inflight: Promise<void> | null = null
    /** Manda a fila em lotes. Chamado durante um envio, devolve o mesmo envio (que ja leva o que entrou depois). */
    function flush(keepalive = false): Promise<void> {
        if (inflight) return inflight
        if (!queue.length) return Promise.resolve()
        inflight = (async () => {
            try {
                while (queue.length) {
                    const batch = queue.slice(0, ACTIVITY_LIMITS.batch)
                    const result = await send(batch, { keepalive }).catch((): SendResult => 'retry')
                    if (result === 'retry') break
                    // 'ok' ou 'drop': o lote sai da fila (recusado nao melhora tentando de novo)
                    queue = queue.slice(batch.length)
                }
            } finally {
                inflight = null
                persist()
            }
        })()
        return inflight
    }

    function closeScreen(at = now()) {
        // Tela de uma aba escondida ja saiu no hide()
        if (!screen || !Number.isFinite(screen.since)) {
            screen = null
            return
        }
        push('screen.viewed', { route: screen.route, ...(screen.target ? { target: screen.target } : {}), durationMs: Math.max(0, at - screen.since), at: screen.since, visitId: screen.visitId })
        screen = null
    }

    return {
        /** Troca de tela: fecha a anterior (com o tempo nela) e abre esta. */
        screen(routeId: string, params: Record<string, unknown> = {}) {
            safe(() => {
                const route = routePattern(routeId)
                const first = Object.values(params).find((v): v is string => typeof v === 'string' && TARGET.test(v))
                if (screen && screen.route === route && screen.target === first) return
                closeScreen()
                screen = { route, ...(first ? { target: first } : {}), since: now(), visitId: visit() }
            }, undefined)
        },
        /** Clique num elemento marcado com data-activity. */
        click(name: string, route?: string) {
            if (!NAME.test(name)) return
            safe(() => push('ui.click', { ...(route ? { route } : {}), metadata: { name } }), undefined)
        },
        /** Aba escondida ou saindo: fecha a tela e manda tudo com keepalive. Ao voltar, a mesma tela abre de novo. */
        hide() {
            const open = screen
            closeScreen()
            if (open) screen = { ...open, since: Number.POSITIVE_INFINITY }
            void flush(true)
        },
        show() {
            if (screen && screen.since === Number.POSITIVE_INFINITY) screen = { ...screen, since: now(), visitId: visit() }
        },
        /** Sai da conta: o que estava na fila ainda vai (com a sessao que acabou, o servidor recusa e cai fora). */
        reset() {
            screen = null
        },
        flush,
        /** Para os testes. */
        pending: () => queue.length,
        current: () => screen,
    }
}

export type ActivityTracker = ReturnType<typeof createActivityTracker>

/** Liga o rastreador na pagina: navegacao do roteador, cliques marcados, aba escondida, rede de volta. */
export function installActivityTracking(tracker: ActivityTracker, { subscribe, isPublic, currentRoute }: {
    subscribe: (fn: (routeId: string, params: Record<string, unknown>, pathname: string) => void) => void
    isPublic: (pathname: string) => boolean
    currentRoute: () => string | undefined
}) {
    safe(() => {
        subscribe((routeId, params, pathname) => {
            if (isPublic(pathname)) tracker.reset()
            else tracker.screen(routeId, params)
        })
        document.addEventListener('click', (event) => {
            const el = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-activity]') : null
            const name = el?.dataset.activity
            if (name) tracker.click(name, currentRoute())
        }, { capture: true, passive: true })
        document.addEventListener('visibilitychange', () => (document.visibilityState === 'hidden' ? tracker.hide() : tracker.show()))
        window.addEventListener('pagehide', () => tracker.hide())
        window.addEventListener('online', () => void tracker.flush())
        setInterval(() => void tracker.flush(), FLUSH_EVERY_MS)
    }, undefined)
}
